import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, Calendar, Clock, ExternalLink } from 'lucide-react';

const CalendarWidget = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const res = await api.get('/events');
      setEvents(res.data || []);
    } catch (e) {
      setEvents([]);
    }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const today = new Date();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0

  const monthNames = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

  // Map events to days
  const eventDays = new Set();
  const eventsByDay = {};
  events.forEach(ev => {
    const startStr = ev.start_time || ev.start_date;
    if (!startStr) return;
    const d = new Date(startStr);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      eventDays.add(day);
      if (!eventsByDay[day]) eventsByDay[day] = [];
      eventsByDay[day].push(ev);
    }
  });

  // Upcoming events this month
  const upcomingEvents = events
    .filter(ev => {
      const d = new Date(ev.start_time || ev.start_date);
      return d >= today && d.getFullYear() === year && d.getMonth() === month;
    })
    .sort((a, b) => new Date(a.start_time || a.start_date) - new Date(b.start_time || b.start_date))
    .slice(0, 3);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const isToday = (day) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  return (
    <div data-testid="calendar-widget-card">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={prevMonth}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-medium">{monthNames[month]} {year}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={nextMonth}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map(d => (
          <div key={d} className="text-[10px] text-center text-muted-foreground font-medium">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for offset */}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        {/* Day cells */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const hasEvent = eventDays.has(day);
          const todayClass = isToday(day);
          return (
            <div
              key={day}
              className={`aspect-square flex flex-col items-center justify-center rounded-md text-xs cursor-pointer transition-colors
                ${todayClass ? 'bg-primary text-primary-foreground font-bold' : ''}
                ${hasEvent && !todayClass ? 'bg-orange-500/20 text-orange-400 font-medium' : ''}
                ${!hasEvent && !todayClass ? 'text-muted-foreground hover:bg-secondary/50' : ''}
              `}
              onClick={() => hasEvent && navigate('/calendar')}
              title={hasEvent ? `${eventsByDay[day].length} evenement(s)` : ''}
            >
              {day}
              {hasEvent && <div className="w-1 h-1 rounded-full bg-orange-500 mt-[1px]" />}
            </div>
          );
        })}
      </div>

      {/* Upcoming events list */}
      {upcomingEvents.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-2">
          {upcomingEvents.map(ev => {
            const d = new Date(ev.start_time || ev.start_date);
            return (
              <div key={ev.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-secondary/30 rounded p-1" onClick={() => navigate(`/calendar/event/${ev.id}`)} data-testid={`calendar-widget-event-${ev.id}`}>
                <div className="w-6 h-6 rounded bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-orange-500">{d.getDate()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{ev.title}</p>
                  <p className="text-muted-foreground">{d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                {ev.event_link && (
                  <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CalendarWidget;
